/**
 * Frontend-only banner name management utility
 * Generates and persists banner names without requiring backend changes
 */

export interface BannerNameData {
  id: string;
  customName?: string;
  generatedName: string;
  timestamp: number;
}

const STORAGE_KEY = 'sfl_banner_names';

class BannerNameManager {
  private bannerNames: Map<string, BannerNameData> = new Map();

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Load banner names from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data: BannerNameData[] = JSON.parse(stored);
        data.forEach(item => {
          this.bannerNames.set(item.id, item);
        });
      }
    } catch (error) {
      console.warn('Failed to load banner names from storage:', error);
    }
  }

  /**
   * Save banner names to localStorage
   */
  private saveToStorage(): void {
    try {
      const data = Array.from(this.bannerNames.values());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save banner names to storage:', error);
    }
  }

  /**
   * Generate a meaningful name for a banner based on its properties
   */
  private generateBannerName(banner: {
    id: string;
    title?: string;
    bannerLocation: 'home' | 'tournament';
    platform?: 'app' | 'web';
    type: 'plain' | 'custom';
    sortNo: number;
  }): string {
    const location = banner.bannerLocation === 'home' ? 'Homepage' : 'Tournament';
    const platform = banner.platform === 'web' ? 'Web' : 'App';
    const type = banner.type === 'custom' ? 'Custom' : 'Plain';
    
    // If banner has a title, use it as base
    if (banner.title && banner.title.trim()) {
      return `${banner.title.slice(0, 30)}${banner.title.length > 30 ? '...' : ''}`;
    }

    // Generate name based on properties: "Homepage Plain Banner - App"
    return `${location} ${type} Banner - ${platform}`;
  }

  /**
   * Get display name for a banner
   */
  getBannerDisplayName(banner: {
    id: string;
    title?: string;
    bannerLocation: 'home' | 'tournament';
    platform?: 'app' | 'web';
    type: 'plain' | 'custom';
    sortNo: number;
  }): string {
    const stored = this.bannerNames.get(banner.id);
    
    // Priority: Custom name -> Stored generated name -> Fresh generated name
    if (stored?.customName) {
      return stored.customName;
    }

    const generatedName = this.generateBannerName(banner);
    
    // Store/update the generated name
    this.setBannerName(banner.id, undefined, generatedName);
    
    return generatedName;
  }

  /**
   * Set custom name for a banner
   */
  setCustomBannerName(bannerId: string, customName: string): void {
    const existing = this.bannerNames.get(bannerId);
    const generatedName = existing?.generatedName || `Unnamed Banner - ${bannerId.slice(-4)}`;
    
    this.bannerNames.set(bannerId, {
      id: bannerId,
      customName: customName.trim() || undefined,
      generatedName,
      timestamp: Date.now()
    });
    
    this.saveToStorage();
  }

  /**
   * Internal method to set banner name data
   */
  private setBannerName(bannerId: string, customName?: string, generatedName?: string): void {
    const existing = this.bannerNames.get(bannerId);
    
    this.bannerNames.set(bannerId, {
      id: bannerId,
      customName: customName || existing?.customName,
      generatedName: generatedName || existing?.generatedName || `Unnamed Banner - ${bannerId.slice(-4)}`,
      timestamp: Date.now()
    });
    
    this.saveToStorage();
  }

  /**
   * Get custom name for editing (returns empty string if none set)
   */
  getCustomBannerName(bannerId: string): string {
    return this.bannerNames.get(bannerId)?.customName || '';
  }

  /**
   * Remove custom name (fallback to generated name)
   */
  removeCustomBannerName(bannerId: string): void {
    const existing = this.bannerNames.get(bannerId);
    if (existing) {
      existing.customName = undefined;
      this.bannerNames.set(bannerId, existing);
      this.saveToStorage();
    }
  }

  /**
   * Clear old entries to prevent localStorage bloat
   * Call periodically to clean up deleted banners
   */
  cleanup(existingBannerIds: string[]): void {
    const existingIds = new Set(existingBannerIds);
    let cleaned = false;
    
    // Use forEach instead of for...of to avoid downlevelIteration issues
    this.bannerNames.forEach((_, id) => {
      if (!existingIds.has(id)) {
        this.bannerNames.delete(id);
        cleaned = true;
      }
    });
    
    if (cleaned) {
      this.saveToStorage();
    }
  }

  /**
   * Search banners by name (includes both custom and generated names)
   */
  searchBanners(banners: any[], searchTerm: string): any[] {
    if (!searchTerm.trim()) {
      return banners;
    }

    const term = searchTerm.toLowerCase();
    return banners.filter(banner => {
      const displayName = this.getBannerDisplayName(banner);
      const title = banner.title || '';
      
      return (
        displayName.toLowerCase().includes(term) ||
        title.toLowerCase().includes(term)
      );
    });
  }
}

// Export singleton instance
export const bannerNameManager = new BannerNameManager();